import React, { useState, useCallback } from "react";
import {
  View,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Text } from "../../../components/Themed";
import Colors from "../../../constants/Colors";
import Toast from "react-native-toast-message";
import { getLocalData, storeLocalData } from "../../../services/storage";
import openhouseAPI from "../../../services/openhouse";
import queryClient from "../../../services/reactQuery";

const NoteField = React.memo(
  ({
    attendee,
    uuid,
    user,
    pingTime,
    updateAttendees,
  }: {
    attendee: any;
    uuid: string;
    user: any;
    pingTime: number | null;
    updateAttendees: (updated: any[]) => void;
  }) => {
    const [note, setNote] = useState(attendee?.note || "");
    const [saving, setSaving] = useState(false);
    const isSynced = attendee?.is_synced;

    const saveNote = useCallback(async () => {
      if (!note || saving) return;

      setSaving(true);
      try {
        if (pingTime === null && !isSynced) {
          const storedAttendees = await getLocalData(`@openHouse+${uuid}`);
          const updatedAttendees = storedAttendees.map((a: any) =>
            a.email + a.phone === attendee.email + attendee.phone
              ? { ...a, note }
              : a
          );
          await storeLocalData(`@openHouse+${uuid}`, updatedAttendees);
          queryClient.setQueryData(
            ["openHouseDetails", uuid],
            (oldData: any) => ({
              ...oldData,
              attendees: updatedAttendees,
            })
          );
          updateAttendees(updatedAttendees);
        } else {
          await openhouseAPI.post("/notes/forAttendee", {
            attendeeUuid: attendee.uuid,
            note,
            organizationUuid: user.organization_uuid,
            userId: user.id,
          });

          const updatedAttendees = await getLocalData(`@openHouse+${uuid}`);
          const syncedAttendees = updatedAttendees.map((a: any) =>
            (a.uuid && a.uuid === attendee.uuid) ||
            (a.local_submission_id &&
              a.local_submission_id === attendee.local_submission_id)
              ? { ...a, note }
              : a
          );
          await storeLocalData(`@openHouse+${uuid}`, syncedAttendees);
          queryClient.setQueryData(
            ["openHouseDetails", uuid],
            (oldData: any) => ({
              ...oldData,
              attendees: syncedAttendees,
            })
          );
          updateAttendees(syncedAttendees);
        }

        Toast.show({
          type: "success",
          text1: "Note Saved",
          text2: "The note has been successfully saved.",
        });
      } catch (error) {
        console.error("Error saving note:", error);
        Toast.show({
          type: "error",
          text1: "Error saving note",
          text2: "There was an error saving the note. Please try again.",
        });
      } finally {
        setSaving(false);
      }
    }, [
      attendee.uuid,
      isSynced,
      note,
      pingTime,
      saving,
      updateAttendees,
      user,
      uuid,
    ]);

    return (
      <View style={{ flex: 1, paddingVertical: 24 }}>
        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
          <Text lightColor={Colors.nuetral_grey_600} size={14}>
            Note
          </Text>
          {!attendee.note &&
            (saving ? (
              <View style={{ flexDirection: "row", gap: 8, marginBottom: 4 }}>
                <ActivityIndicator
                  size="small"
                  color={Colors.nuetral_grey_600}
                />
                <Text
                  lightColor={Colors.nuetral_grey_600}
                  size={14}
                  weight="Bold"
                >
                  Saving...
                </Text>
              </View>
            ) : (
              <TouchableOpacity onPress={saveNote} disabled={!note.trim()}>
                <Text
                  lightColor={Colors.primary_500}
                  darkColor={Colors.primary_500}
                  size={14}
                  weight="Bold"
                >
                  Save
                </Text>
              </TouchableOpacity>
            ))}
        </View>

        {attendee.note ? (
          <Text
            lightColor={Colors.nuetral_grey_700}
            style={{ marginBottom: 8 }}
          >
            {attendee.note}
          </Text>
        ) : (
          <TextInput
            multiline
            editable={!attendee.note}
            value={note}
            onChangeText={setNote}
            placeholder="Add a note..."
            style={{
              backgroundColor: Colors.nuetral_grey_100,
              borderRadius: 8,
              padding: 8,
              height: 80,
              color: Colors.nuetral_grey_700,
              marginBottom: 8,
              borderColor: Colors.nuetral_grey_400,
              borderWidth: 1,
            }}
          />
        )}

        <Text
          lightColor={Colors.nuetral_grey_600}
          darkColor={Colors.nuetral_grey_600}
          size={12}
        >
          One note can be added per lead and cannot be edited once saved. Note
          updates can be made in Brivity GO or Brivity CRM, but changes won’t
          appear here.
        </Text>
      </View>
    );
  }
);

export default NoteField;
